/**
 * favorites — generalized per-user favorites store backed by `/api/v2/favorites`.
 *
 * Each entry: { id, kind, label, to, icon, favorited_at }
 *
 * Stored in Elasticsearch on the backend; mirrored on the client via a
 * module-level reactive Map. Supported kinds: `tool`, `dashboard`,
 * `custom_dashboard`, `page`, ... — the kind is metadata for chip rendering;
 * uniqueness is per `id`.
 *
 * NOTE: state is intentionally read via `loadFavorites()` rather than a shared
 * import. Webpack's HMR can produce duplicate module instances during dev
 * which would split the ref between consumers; the consumers fetch the latest
 * snapshot through the API or use the local `favorites` ref returned by
 * `useFavorites()`.
 */
import FavoritesRepository from '@/repositories/FavoritesRepository';
import { computed, ref } from 'vue';

// HTTP lives in `FavoritesRepository`, which keeps the deferred client import
// (`AuthenticatedClient` pulls the global i18n instance / `createI18n` at module
// init; some test envs mock `vue-i18n` without exporting `createI18n`). The
// repo's lazy import keeps eager consumers (e.g. `FavoriteToggle` from
// `StandardToolPage`) renderable in those tests. State + composable stay here.

const favoritesById = ref(new Map());

function applyItems(items) {
  const next = new Map();
  for (const item of items || []) {
    if (item && item.id) next.set(item.id, item);
  }
  favoritesById.value = next;
}

export async function loadFavorites() {
  try {
    const response = await FavoritesRepository.list();
    applyItems(response?.data?.items || []);
    return Array.from(favoritesById.value.values());
  } catch (error) {
    return [];
  }
}

/**
 * Add or update a favorite.
 * @param {{id: string, kind: string, label: string, to: string, icon?: string|null}} entry
 */
export async function addFavorite(entry) {
  if (!entry || !entry.id || !entry.kind || !entry.label || !entry.to) return;
  const next = new Map(favoritesById.value);
  next.set(entry.id, {
    ...entry,
    favorited_at: favoritesById.value.get(entry.id)?.favorited_at || new Date().toISOString(),
  });
  favoritesById.value = next;
  try {
    const response = await FavoritesRepository.put(entry.id, {
      kind: entry.kind,
      label: entry.label,
      to: entry.to,
      icon: entry.icon || null,
    });
    if (response?.data) {
      const merged = new Map(favoritesById.value);
      merged.set(entry.id, response.data);
      favoritesById.value = merged;
    }
  } catch (error) {
    const reverted = new Map(favoritesById.value);
    reverted.delete(entry.id);
    favoritesById.value = reverted;
    throw error;
  }
}

export async function removeFavorite(id) {
  if (!id) return;
  const previous = favoritesById.value.get(id);
  const next = new Map(favoritesById.value);
  next.delete(id);
  favoritesById.value = next;
  try {
    await FavoritesRepository.remove(id);
  } catch (error) {
    if (previous) {
      const reverted = new Map(favoritesById.value);
      reverted.set(id, previous);
      favoritesById.value = reverted;
    }
    throw error;
  }
}

export function isFavorite(id) {
  return favoritesById.value.has(id);
}

export function getFavorite(id) {
  return favoritesById.value.get(id) || null;
}

export function useFavorites() {
  const favorites = computed(() => Array.from(favoritesById.value.values()));
  const favoriteIds = computed(() => Array.from(favoritesById.value.keys()));
  return { favorites, favoriteIds, favoritesById, loadFavorites, addFavorite, removeFavorite, isFavorite, getFavorite };
}

export { favoritesById };

export default {
  favoritesById,
  loadFavorites,
  addFavorite,
  removeFavorite,
  isFavorite,
  getFavorite,
  useFavorites,
};
